import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

export default function CancelOrderModal({ isOpen, orderId, isPaid, onConfirm, onCancel }) {
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setReason('');
            setCustomReason('');
            setLoading(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        const finalReason = reason === 'Other' ? customReason : reason;
        setLoading(true);
        await onConfirm(finalReason);
        setLoading(false);
    };

    const isConfirmDisabled = !reason || (reason === 'Other' && !customReason.trim()) || loading;

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!loading ? onCancel : undefined}></div>

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 animate-scale-in">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 w-full">
                        <h3 className="text-lg font-semibold text-gray-900">Cancel Order #{orderId}</h3>
                        
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation</label>
                                <select 
                                    value={reason} 
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                                    disabled={loading}
                                >
                                    <option value="">Select a reason...</option>
                                    <option value="Changed my mind">Changed my mind</option>
                                    <option value="Ordered by mistake">Ordered by mistake</option>
                                    <option value="Delivery time too long">Delivery time too long</option>
                                    <option value="Want to modify the order">Want to modify the order</option>
                                    <option value="Found a better price">Found a better price</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            {reason === 'Other' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Please specify</label>
                                    <textarea 
                                        value={customReason}
                                        onChange={(e) => setCustomReason(e.target.value)}
                                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                        rows="3"
                                        placeholder="Tell us why you are cancelling..."
                                        disabled={loading}
                                    />
                                </div>
                            )}

                            <p className={`text-sm font-medium ${isPaid ? 'text-amber-600' : 'text-gray-500'}`}>
                                {isPaid ? 'A refund will be initiated to your original payment method.' : 'This action cannot be undone.'}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                    >
                        Keep Order
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[120px]"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Order'}
                    </button>
                </div>
            </div>
        </div>
    );
}
